/**
 * useAcademyChatSession — initialization hook for the Academy AI Assistant.
 *
 * On mount, fetches in parallel:
 *  1. The encrypted system config (useGetEncryptedConfigQuery) — tells the Copilot
 *     backend which assistant persona and knowledge base to use.
 *  2. A new chat thread (useStartChatMutation) — returns the thread ID and the Ably
 *     channel ID to subscribe to.
 *  3. The current user's ID (userInfo) — needed for Ably token auth.
 *
 * Once all three resolve, `isReady` flips to true and the consumer can render
 * an <AcademyChatProvider> with the returned thread / userId.
 */
import { useEffect, useState } from 'react';
import userInfo from 'hub-http/userInfo';
import { useGetEncryptedConfigQuery, useGetEncryptedConfigWithModifiedSystemPromptQuery } from '../../__generated__/chirp/com/hubspot/academy/assistant/rpc/ChatRpc.hooks';
import { useStartChatMutation } from '../../__generated__/chirp/com/hubspot/copilot/rpc/CopilotChatService.hooks';
import I18n from 'I18n';
export function useAcademyChatSession(options = {}) {
  var _ref, _ref2, _configError$message, _chatData$thread, _configData$value;
  const {
    modifiedSystemPrompt
  } = options;
  const hasModifiedPrompt = modifiedSystemPrompt !== undefined;
  const [userId, setUserId] = useState(null);
  const [userInfoError, setUserInfoError] = useState(null);
  const {
    data: defaultConfigData,
    loading: defaultConfigLoading,
    error: defaultConfigError
  } = useGetEncryptedConfigQuery({
    skip: hasModifiedPrompt
  });
  const {
    data: modifiedConfigData,
    loading: modifiedConfigLoading,
    error: modifiedConfigError
  } = useGetEncryptedConfigWithModifiedSystemPromptQuery({
    variables: {
      systemPrompt: modifiedSystemPrompt
    },
    skip: !hasModifiedPrompt
  });
  const configData = hasModifiedPrompt ? modifiedConfigData : defaultConfigData;
  const configLoading = hasModifiedPrompt ? modifiedConfigLoading : defaultConfigLoading;
  const configError = hasModifiedPrompt ? modifiedConfigError : defaultConfigError;
  const [startChat, {
    data: chatData,
    loading: chatLoading,
    error: chatError
  }] = useStartChatMutation();
  const handleError = err => {
    setUserInfoError(err.message || I18n.text('academy-ai-assistant.errors.startChat'));
  };
  useEffect(() => {
    let cancelled = false;
    startChat({
      variables: {}
    }).catch(handleError);
    userInfo().then(response => {
      if (!cancelled) {
        setUserId(response.user.user_id);
      }
    }).catch(handleError);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const error = (_ref = (_ref2 = (_configError$message = configError === null || configError === void 0 ? void 0 : configError.message) !== null && _configError$message !== void 0 ? _configError$message : chatError === null || chatError === void 0 ? void 0 : chatError.message) !== null && _ref2 !== void 0 ? _ref2 : userInfoError) !== null && _ref !== void 0 ? _ref : null;
  const isReady = !configLoading && !chatLoading && configData !== undefined && chatData !== undefined && userId !== null && error === null;
  return {
    thread: (_chatData$thread = chatData === null || chatData === void 0 ? void 0 : chatData.thread) !== null && _chatData$thread !== void 0 ? _chatData$thread : null,
    systemConfig: (_configData$value = configData === null || configData === void 0 ? void 0 : configData.value) !== null && _configData$value !== void 0 ? _configData$value : null,
    userId,
    error,
    isReady
  };
}